// @flow
import type {HttpService} from 'modules/common/services/http';
import type {TChangeAccountDataParams, TNewPasswordDetailsParams, TUpdateCompanyDataParams} from 'modules/settings/types';
import type {TResponse} from 'types';

/**
 * Сервис для загрузки информации о настройках
 */
export class SettingsApi {
    /**
     * Сервис для отправки HTTP запросов
     */
    _http: HttpService;

    /**
     * @param http Сервис для отправки HTTP запросов
     */
    constructor(http: HttpService) {
        this._http = http;
    }

    /**
     * Fetch запрос "Запросить данные о компании к которой относится админ"
     * @return список всех пользователей
     */
    async getCompanyData<T>(): TResponse<T> {
        return this._http.get<T>('/company', {
            extraOptions: {
                isPrivate: true,
            },
        });
    }

    /**
     * Fetch запрос "Изменить данные о компании к которой относится админ"
     * @param companyData объект с новыми данными компании
     * @return обновленные данные компании
     */
    async updateCompanyData<T>(companyData: TUpdateCompanyDataParams): TResponse<T> {
        return this._http.post<T>('/company/update', {
            body: companyData,
            extraOptions: {
                dataType: 'FORM_DATA',
                isPrivate: true,
            },
            options: {
                headers: {
                    'X-http-method-override': 'POST',
                },
            },
        });
    }

    /**
     * Fetch запрос "Запросить данные о последнем обновлении сервера"
     * @return данные о последнем обновлении сервера
     */
    async getLastServerUpdate<T>(): TResponse<T> {
        return this._http.get<T>('/server/lastUpdate', {
            extraOptions: {
                isPrivate: true,
            },
        });
    }

    /**
     * Fetch запрос "Обновить сервер"
     * @return данные о последнем обновлении сервера
     */
    async updateServer<T>(): TResponse<T> {
        return this._http.post<T>('/server/update', {
            extraOptions: {
                isPrivate: true,
            },
            options: {
                headers: {
                    'X-http-method-override': 'POST',
                },
            },
        });
    }

    /**
     * Fetch запрос "Изменить данные аккаунта"
     * @param newAccountData данные аккаунта для изменения
     * @return обновленные данные аккаунта пользователя
     */
    async changeAccountData<T>({userId, email, firstName, languageId, activeLanguageId}: TChangeAccountDataParams): TResponse<T> {
        return this._http.post<T>(`/user/update/${userId}`, {
            body: {activeLanguageId, email, firstName, languageId},
            extraOptions: {
                isPrivate: true,
            },
            options: {
                headers: {
                    'Content-Type': 'application/json',
                    'X-http-method-override': 'POST',
                },
            },
        });
    }

    /**
     * Fetch запрос "Изменить пароль аккаунта"
     * @param newPasswordDetails данные для изменения пароля
     * @return обновленные данные аккаунта пользователя
     */
    async changeAccountPassword<T>(newPasswordDetails: TNewPasswordDetailsParams): TResponse<T> {
        return this._http.post<T>('/user/change-password', {
            body: newPasswordDetails,
            extraOptions: {
                isPrivate: true,
            },
            options: {
                headers: {
                    'Content-Type': 'application/json',
                    'X-http-method-override': 'POST',
                },
            },
        });
    }
}
